// src/components/AIGuestAssistant.js
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';




export default function AIGuestAssistant() {
    const { t } = useTranslation();
    const suggestedQuestions = [
        t('ai_assistant.q1'),
        t('ai_assistant.q2'),
        t('ai_assistant.q3'),
        t('ai_assistant.q4'),
    ];
    const initialMessages = [
        { type: 'bot', text: t('ai_assistant.welcome') },
    ];
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState(initialMessages);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    
    const messagesEndRef = useRef(null);
    


    // Scroll to the bottom of the chat window when messages change
    useEffect(() => {
        if (isOpen) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, isOpen]);
    
    // Function to handle suggested questions
    const handleSuggestedQuestionClick = (question) => {
        if (isTyping) return;
        setInput(''); 
        handleSend(question); 
    };

    // Modified handleSend to accept an optional question string
    const handleSend = (suggestedText = null) => {
        const textToSend = suggestedText || input.trim();
        if (textToSend === '') return;

        const newMessage = { type: 'user', text: textToSend };
        
        // 1. Add user message
        setMessages(prev => [...prev, newMessage]);
        setInput('');
        setIsTyping(true);

        // 2. Simulate AI response (PLACEHOLDER LOGIC)
        setTimeout(() => {
            // PROFESSIONAL FALLBACK MESSAGE (Used when no specific keyword is found)
            let botResponse = t('ai_assistant.fallback', { defaultValue: "I am ready to help you with our digital services. Can you ask me about Check-in, Room Service, or your local recommendations?" });
            
            // Basic keyword responses focused on the DIGITAL PLATFORM
            const userText = textToSend.toLowerCase(); 
            
            if (userText.includes('check-in page') || userText.includes('digital check-in')) {
                // Digital Check-in guidance
                botResponse = t('ai_assistant.resp_checkin', { defaultValue: "To complete your digital check-in, please navigate to the **'My Stay'** page. You will need to upload your ID/Passport and confirm payment." });
            } else if (userText.includes('check-out process') || userText.includes('digital check-out')) {
                // Digital Check-out guidance
                botResponse = t('ai_assistant.resp_checkout', { defaultValue: "Digital Check-out is simple. Go to the **'My Stay'** page and click the **'Request Check-Out' button**." });
            } else if (userText.includes('wi-fi') || userText.includes('password')) {
                // Standard amenity question
                botResponse = t('ai_assistant.resp_wifi', { defaultValue: "The Wi-Fi network is **'HotelGuest'** and the password is **'WelcomeHome24'**." });
            } else if (userText.includes('room service') || userText.includes('full menu')) {
                // Directing them to the digital menu
                botResponse = t('ai_assistant.resp_menu', { defaultValue: "The complete Room Service menu is available in the **'Dining' section** of this application." });
            } else if (userText.includes('amenities') || userText.includes('maintenance') || userText.includes('request item')) {
                // Directing them to the request system
                botResponse = t('ai_assistant.resp_staff', { defaultValue: "You can request extra amenities or report maintenance issues directly through the **'Staff Requests'** feature." });
            } else if (userText.includes('upgrade')) {
                // Upsell is still useful
                botResponse = "We have a few Premium Suites with sea views still available! You can view upgrade options on the **'Booking Details'** page, or speak to the Front Desk team for a personalized offer.";
            }


            const botMessage = { type: 'bot', text: botResponse };
            
            setMessages(prev => [...prev, botMessage]);
            setIsTyping(false);
        }, 1500);
    };

    // Styling object for the chatbot component (unchanged)
    const chatStyles = {
        chatContainer: {
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
        },
        chatIcon: {
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#007bff',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '1.5em',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            transition: 'transform 0.3s ease',
        },
        chatWindow: {
            position: 'absolute',
            bottom: '80px',
            right: '0',
            width: '320px',
            height: '400px',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'opacity 0.3s, transform 0.3s',
        },
        chatHeader: {
            padding: '10px',
            backgroundColor: '#007bff',
            color: 'white',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        messageArea: {
            flexGrow: 1,
            padding: '10px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
        },
        inputArea: {
            display: 'flex',
            borderTop: '1px solid #eee',
            padding: '10px',
        },
        messageBubble: (type) => ({
            maxWidth: '80%',
            padding: '8px 12px',
            borderRadius: '18px',
            backgroundColor: type === 'bot' ? '#f1f1f1' : '#007bff',
            color: type === 'bot' ? '#333' : 'white',
            alignSelf: type === 'bot' ? 'flex-start' : 'flex-end',
            wordWrap: 'break-word',
        }),
        chatInput: {
            flexGrow: 1,
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '20px',
            marginRight: '8px',
        },
        sendButton: {
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '35px',
            height: '35px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
        },
        suggestedButton: {
            padding: '8px 12px',
            margin: '5px',
            border: '1px solid #007bff',
            borderRadius: '20px',
            backgroundColor: 'white',
            color: '#007bff',
            cursor: 'pointer',
            fontSize: '0.9em',
            textAlign: 'left',
            width: '100%',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
            transition: 'background-color 0.2s',
        }
    };

    return (
        <div style={chatStyles.chatContainer}>
            {isOpen && (
                <div style={chatStyles.chatWindow}>
                    <div style={chatStyles.chatHeader}>
                        <span>{t('guest_dash.chat_title')}</span>
                        <button 
                            onClick={() => setIsOpen(false)} 
                            style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.2em', cursor: 'pointer' }}
                        >
                            &times;
                        </button>
                    </div>
                    <div style={chatStyles.messageArea}>
                        {messages.map((msg, index) => (
                            <div key={index} style={chatStyles.messageBubble(msg.type)}>
                                {msg.text}
                            </div>
                        ))}
                        
                        {/* SUGGESTED QUESTIONS BLOCK */}
                        {/* Show suggestions only if the chat is open and only the initial welcome message is present */}
                        {messages.length === 1 && (
                            <div style={{ marginTop: '10px', padding: '5px', alignSelf: 'flex-start', width: '90%' }}>
                                <p style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: '#555', fontWeight: 'bold' }}>{t('home.access_points')}:</p>
                                {suggestedQuestions.map((q, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSuggestedQuestionClick(q)} 
                                        style={chatStyles.suggestedButton}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f0ff'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                        disabled={isTyping}
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                        )}

                        {isTyping && (
                            <div style={chatStyles.messageBubble('bot')}>
                                {t('ai_assistant.typing')}
                            </div>
                        )}
                        <div ref={messagesEndRef} /> 
                    </div>
                    <div style={chatStyles.inputArea}>
                        <input
                            type="text"
                            placeholder={t('ai_assistant.placeholder')}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSend();
                            }}
                            style={chatStyles.chatInput}
                            disabled={isTyping}
                        />
                        <button 
                            onClick={() => handleSend()} 
                            disabled={isTyping || input.trim() === ''}
                            style={chatStyles.sendButton}
                        >
                            ▶
                        </button>
                    </div>
                </div>
            )}
            <div 
                style={{
                    ...chatStyles.chatIcon,
                    backgroundColor: isOpen ? '#dc3545' : '#007bff',
                }} 
                onClick={() => setIsOpen(prev => !prev)}
            >
                {isOpen ? '—' : '💬'}
            </div>
        </div>
    );
}